import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ErrorCode } from './errorCode';
import { ErrorResponse } from '../dto/ResDtoRepository';
import { getConfig } from '../env/envManager';
import { isNetworkError } from '../util/isNetworkError';
import { useAuthTokenStore } from '../store/authStore';
import { refresh } from './authAPI';

// 정리
// publicApi:     인터셉터 x, cookie 자동세팅 x
// authApi:       인터셉터 o, cookie 자동세팅 x
// withCookieApi: 인터셉터 x, cookie 자동세팅 o

// 사용처
// publicApi: signup + 그냥 기본 get 요청, aws 요청
// authApi: 인증이 필요한 write 요청, ower get 요청
// withCookieApi: login, activate, refresh, logout

const config = getConfig();

// =========================  
// authToken을 사용하는 authApi: 인터셉터 o, cookie 자동세팅 x
// =========================
const authApi = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ------------------------------------------------------------------
// 1) 요청 인터셉터: AccessToken 헤더 주입
// ------------------------------------------------------------------
authApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const authToken = useAuthTokenStore.getState().authToken;
  if (authToken && config.headers) {
    config.headers.Authorization = `${authToken}`;
  }
  return config;
});

// ------------------------------------------------------------------
// 2) 응답 인터셉터: ATX 처리 & 자동 재시도
// ------------------------------------------------------------------
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let failedQueue: ((token?: string) => void)[] = [];

function processQueue(token?: string) {
  failedQueue.forEach(cb => cb(token));
  failedQueue = [];
}

authApi.interceptors.response.use(
  res => res,
  async (error: AxiosError<any>) => {
    const { config, response } = error;
    if (!response) throw error;  // 네트워크 에러

    const origReq = config as InternalAxiosRequestConfig & { _retry?: boolean };
    const isATX = response.status === 401 && response.data?.errorCode === 'ATX';

    // ATX가 아니거나 이미 재시도한 요청이면 밖으로 throw
    if (!isATX || origReq._retry) {
      throw error;
    }

    // ---------- 여기부터 ATX 처리 ----------
    if (!isRefreshing) {
      isRefreshing = true;

      refreshPromise = (async () => {
        try {
          const response = await refresh();
          const { value } = response.data;
          if (!value) {
            throw new Error('Access token not found in response');
          };
          useAuthTokenStore.getState().setAuthToken(value);
          return value;
        } catch (e) {
          throw e;
        } finally {
          isRefreshing = false;
        }
      })();
    }

    // 모든 실패한 요청 큐에 넣어서 토큰 받아오면 재시도???
    return new Promise((resolve, reject) => {
      failedQueue.push(async (tokenOrError?: string | Error) => {
        if (!tokenOrError || tokenOrError instanceof Error) {
          reject(tokenOrError);
          return;
        }
        try {
          origReq.headers.Authorization = `${tokenOrError}`;
          origReq._retry = true;
          resolve(authApi(origReq));
        } catch (err) {
          reject(err);
        }
      });

      refreshPromise!
        .then((newToken) => processQueue(newToken))
        .catch((err) => processQueue(err));
    });
  }
);

// =========================  
// API 함수들 (통합된 authApi 사용)
// =========================
type HttpMethod = 'get' | 'post' | 'put';

export async function sendApiRequest<T>(
  method: HttpMethod,
  endpoint: string,
  data?: any
): Promise<T> {
  try {
    const response = await authApi[method]<T>(endpoint, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function sendDeleteApiRequest<T>(
  endpoint: string,
): Promise<T> {
  try {
    const response = await authApi.delete<T>(endpoint);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// =========================  
// publicApi: 인터셉터 x, cookie 자동세팅 x
// =========================
export const publicApi = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  }
});

export async function sendPolarApiRequest<T>(
  method: HttpMethod,
  endpoint: string,
  data?: any
): Promise<T> {
  try {
    const response = await publicApi[method](endpoint, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function sendAWSApiRequest<T>(
  method: HttpMethod,
  endpoint: string,
  data: File
): Promise<T> {
  try {
    const response = await publicApi[method]<T>(endpoint, data, {
      headers: { 'Content-Type': data.type }
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

export async function sendGetApiRequest<T>(
  endpoint: string,
): Promise<T> {
  try {
    const response = await publicApi.get<T>(endpoint);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// =========================  
// refreshToken을 사용하는 withCookieApi: 인터셉터 x, cookie 자동세팅 o
// =========================
export const withCookieApi = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,      //  refresh 쿠키 자동 전송
});

const formatErrorMessage = (code: string, message: string): string => {
  return `${code}:: ${message}`;
};

export const handleApiError = (error: unknown): Error => {
  if (isNetworkError(error)) {
    return new Error(formatErrorMessage(ErrorCode.NCE, 'Network Error'));
  }
  if (axios.isAxiosError(error)) {
    if (error.code === 'ERR_NETWORK') {
      return new Error(formatErrorMessage(ErrorCode.SDN, 'Something wrong in Server'));
    }
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      return new Error(formatErrorMessage(
        axiosError.response.data.errorCode,
        axiosError.response.data.message
      ));
    }
  }
  const rawErrorMessage = error instanceof Error ? error.message : 'not an Error instance';
  if (rawErrorMessage.includes('::')) {
    const [errorCode, errorMessage] = rawErrorMessage.split('::').map(str => str.trim());
    return new Error(formatErrorMessage(errorCode, errorMessage));
  }

  return new Error(formatErrorMessage(ErrorCode.WEF, 'Unexpected Error'));
};